import type {
  APIPlanDefinitionRequestType,
  APIPlanDefinitionResponseType,
  APIPlanDefinitionStatus,
  APIPlanResolvedAccessPresetsType,
  APIPlanVersionResponseType,
} from "@/core/data_sources/models/APIPlanDefinition";
import type { BasicQuery } from "@/core/data_sources/services";
import { get, patch, post } from "@/core/utils/request";

export type PlanDefinitionsQuery = BasicQuery &
  Partial<{
    search: string;
    status: APIPlanDefinitionStatus;
  }>;

export type PlanDefinitionDetailQuery = BasicQuery & {
  id: string | number;
};

export type PlanAccessPresetsQuery = PlanDefinitionDetailQuery &
  Partial<{
    version: number;
  }>;

export type PlanVersionsQuery = BasicQuery & {
  id: string | number;
};

export function getPlanDefinitions<Q extends PlanDefinitionsQuery>(query: Q) {
  return get<APIPlanDefinitionResponseType[]>("/super-admin/plan-definition/", {
    params: query,
  });
}

export function getPlanDefinitionById(id: string | number) {
  return get<APIPlanDefinitionResponseType>(
    `/super-admin/plan-definition/${id}/`,
  );
}

export function getPlanDefinitionByQuery<Q extends PlanDefinitionDetailQuery>(
  query: Q,
) {
  return getPlanDefinitionById(query.id);
}

export function postPlanDefinition(data: APIPlanDefinitionRequestType) {
  return post<APIPlanDefinitionRequestType, APIPlanDefinitionResponseType>(
    "/super-admin/plan-definition/",
    data,
  );
}

export function patchPlanDefinition(
  id: string | number,
  data: APIPlanDefinitionRequestType,
) {
  return patch<APIPlanDefinitionRequestType, APIPlanDefinitionResponseType>(
    `/super-admin/plan-definition/${id}/`,
    data,
  );
}

export function publishPlanDefinition(id: string | number) {
  return post<undefined, APIPlanDefinitionResponseType>(
    `/super-admin/plan-definition/${id}/publish/`,
  );
}

export function getPlanAccessPresets<Q extends PlanAccessPresetsQuery>(
  query: Q,
) {
  const { id, ...params } = query;
  return get<APIPlanResolvedAccessPresetsType>(
    `/super-admin/plan-definition/${id}/access-presets/`,
    { params },
  );
}

export function getPlanVersions(id: string | number) {
  return get<APIPlanVersionResponseType[]>(
    `/super-admin/plan-definition/${id}/versions/`,
  );
}

export function getPlanVersionsByQuery<
  Q extends { id: string | number } & BasicQuery,
>(query: Q) {
  return getPlanVersions(query.id);
}
